import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "./store";
import { RoleHasPermission } from "@/types/auth.types";

interface UserData {
  email: string;
  name?: string;
  id?: string;
  user_type?: string;
  roles?: string[];
}

export interface AuthState {
  token: string | null;
  profile_picture: string | null;
  language: string;
  userType: string | null;
  userData: UserData | null;
  selectedHarborId: string | null;
  selectedCommunityId: string | null;
  selectedServiceProviderId?: string | null;
  userPermissions: RoleHasPermission[] | null;
  loggedOutDueToError: boolean;
}

interface LoginPayload {
  token: string;
}

const initialState: AuthState = {
  token: null,
  profile_picture: null,
  language: "en",
  userType: null,
  userData: null,
  selectedHarborId: null,
  selectedCommunityId: null,
  selectedServiceProviderId: null,
  userPermissions: null,
  loggedOutDueToError: false,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    loginReducer: (state, action: PayloadAction<LoginPayload>) => {
      state.token = action.payload.token;
      state.loggedOutDueToError = false;
      localStorage.setItem("token", action.payload.token);
    },
    logout: (state) => {
      state.token = null;
      localStorage.removeItem("token");
    },
    logoutDueToError: (state) => {
      state.token = null;
      state.loggedOutDueToError = true;
      localStorage.removeItem("token");
    },
    setProfilePic: (
      state,
      action: PayloadAction<{ profile_picture?: string }>,
    ) => {
      state.profile_picture = action.payload.profile_picture ?? null;
    },
    setLanguage: (state, action: PayloadAction<string>) => {
      state.language = action.payload;
    },
    setUserType: (state, action: PayloadAction<{ user_type?: string }>) => {
      state.userType = action.payload.user_type ?? null;
    },
    setUserData: (state, action: PayloadAction<{ userData?: UserData }>) => {
      state.userData = action.payload.userData ?? null;
    },
    setSelectedHarborId: (
      state,
      action: PayloadAction<{ selectedHarborId?: string }>,
    ) => {
      state.selectedHarborId = action.payload.selectedHarborId ?? null;
    },
    setSelectedCommunityId: (
      state,
      action: PayloadAction<{ selectedCommunityId?: string }>,
    ) => {
      state.selectedCommunityId = action.payload.selectedCommunityId ?? null;
    },
    setSelectedServiceProviderId: (
      state,
      action: PayloadAction<{ selectedServiceProviderId?: string }>,
    ) => {
      state.selectedServiceProviderId =
        action.payload.selectedServiceProviderId ?? null;
    },
    setUserPermissions: (
      state,
      action: PayloadAction<{ permissions?: RoleHasPermission[] }>,
    ) => {
      state.userPermissions = action.payload.permissions ?? null;
    },
  },
});

export const {
  loginReducer,
  logout,
  logoutDueToError,
  setProfilePic,
  setLanguage,
  setUserType,
  setUserData,
  setSelectedHarborId,
  setSelectedCommunityId,
  setSelectedServiceProviderId,
  setUserPermissions,
} = authSlice.actions;
export default authSlice.reducer;

export const selectCurrentToken = (state: RootState) => state.auth.token;
export const selectSelectedCommunityId = (state: RootState) =>
  state.auth.selectedCommunityId;
export const selectUserPermissions = (state: RootState) =>
  state.auth.userPermissions;
